"use client";

import Link from "next/link";
import { SectionLink } from "@/components/SectionLink";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { ArrowRight, Menu, X } from "lucide-react";
import { useMotionValueEvent, useScroll } from "motion/react";
import { Button } from "@/components/ui/button";
import { navLinks } from "@/lib/site";
import { cn } from "@/lib/utils";

// Sections on the home page that have their own nav link ("/#how-it-works" -> "how-it-works").
const sectionIds = navLinks.flatMap((link) => (link.href.startsWith("/#") ? [link.href.slice(2)] : []));

export function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [section, setSection] = useState<string | null>(null);
  const pathname = usePathname();
  const { scrollY } = useScroll();

  // Section links can't match the path, so track which section crosses a line
  // 40% down the viewport instead.
  const updateSection = useCallback(() => {
    if (pathname !== "/") return setSection(null);
    const line = window.innerHeight * 0.4;
    const current = sectionIds.find((id) => {
      const rect = document.getElementById(id)?.getBoundingClientRect();
      return rect && rect.top <= line && rect.bottom > line;
    });
    setSection(current ?? null);
  }, [pathname]);

  useMotionValueEvent(scrollY, "change", (y) => {
    setScrolled(y > 8);
    updateSection();
  });

  useEffect(() => {
    setOpen(false);
    updateSection();
  }, [pathname, updateSection]);

  const isActive = (href: string) =>
    href.startsWith("/#") ? section === href.slice(2) : href === pathname;

  return (
    <header
      className={cn(
        "light sticky top-0 z-50 border-b bg-background text-foreground transition-colors duration-300",
        scrolled || open ? "border-border" : "border-transparent",
      )}
    >
      <div className="container-page flex h-16 items-center justify-between gap-6">
        <Link href="/" className="flex items-center gap-2.5 text-lg font-medium tracking-tight">
          <Image src="/favicon.svg" alt="" width={28} height={28} className="shrink-0" />
          REGRADEIT
        </Link>

        <nav aria-label="Main" className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => {
            const active = isActive(link.href);
            return (
              <SectionLink
                key={link.href}
                href={link.href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "rounded-full px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-surface hover:text-foreground",
                  active && "bg-primary-container text-primary-on-container hover:bg-primary-container",
                )}
              >
                {link.label}
              </SectionLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <Button asChild variant="brand" className="px-4 sm:px-6">
            <Link href="/contact">
              Become a Partner <ArrowRight className="hidden sm:block" aria-hidden />
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-foreground lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X /> : <Menu />}
          </Button>
        </div>
      </div>

      {open && (
        <nav aria-label="Mobile" className="border-t border-border bg-background lg:hidden">
          <div className="container-page flex flex-col py-3">
            {navLinks.map((link) => (
              <SectionLink
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                aria-current={isActive(link.href) ? "page" : undefined}
                className={cn(
                  "rounded-full px-4 py-3 text-base hover:bg-surface",
                  isActive(link.href) && "bg-primary-container text-primary-on-container hover:bg-primary-container",
                )}
              >
                {link.label}
              </SectionLink>
            ))}
            <Button asChild variant="brand" size="lg" className="mt-3">
              <Link href="/contact">Become a Partner</Link>
            </Button>
          </div>
        </nav>
      )}
    </header>
  );
}
